import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CanActivateFn, Router } from '@angular/router';
import {
  MzicArtistLocalService,
  MzicLoginStateService,
  MzicTokenLocalService,
  RegistrationStateService,
} from '@mzic/mzic-services';

/**
 * Guard that manages incomplete registration state and prevents bypassing of required steps
 */
export const incompleteRegistrationGuard: CanActivateFn = (route, state) => {
  const tokenService = inject(MzicTokenLocalService);
  const artistLocalService = inject(MzicArtistLocalService);
  const loginStateService = inject(MzicLoginStateService);
  const registrationStateService = inject(RegistrationStateService);
  const router = inject(Router);
  const dialog = inject(MatDialog);

  const currentPath = state.url;
  const hasIncompleteRegistration = tokenService.hasIncompleteRegistration();
  const isIncompleteRegistrationExpired =
    tokenService.isIncompleteRegistrationExpired();

  // If registration is expired, clean everything
  if (isIncompleteRegistrationExpired) {
    // Close all open dialogs before redirecting
    dialog.closeAll();

    cleanupIncompleteRegistration('session_expired', {
      tokenService,
      artistLocalService,
      loginStateService,
      registrationStateService,
    });
    router.navigate(['/login']);
    return false;
  }

  // If user has incomplete registration
  if (hasIncompleteRegistration) {
    const isTermsPage =
      currentPath.includes('/create-account/artist/terms-and-privacy-policy') ||
      currentPath.includes('/create-account/label/terms-and-privacy-policy');
    const isSuccessPage =
      currentPath.includes('/create-account/artist/success-create-account') ||
      currentPath.includes('/create-account/label/success-create-account');

    // Only allow access to terms and success pages
    if (!isTermsPage && !isSuccessPage) {
      console.log(
        `[Incomplete Registration Guard] Blocking access to: ${currentPath}`,
      );

      // If user tries to access any other route, it's a bypass attempt
      if (currentPath !== '/login' && currentPath !== '/home') {
        cleanupIncompleteRegistration('route_bypass_attempt', {
          tokenService,
          artistLocalService,
          loginStateService,
          registrationStateService,
        });
        router.navigate(['/login']);
        return false;
      }

      // Redirect to terms page to complete registration
      const currentLang = getCurrentLanguage(currentPath);
      router.navigate([
        `${currentLang}/create-account/artist/terms-and-privacy-policy`,
      ]);
      return false;
    }
  }

  return true;
};

/**
 * Cleans up all incomplete registration data
 */
function cleanupIncompleteRegistration(
  reason: string,
  services: {
    tokenService: MzicTokenLocalService;
    artistLocalService: MzicArtistLocalService;
    loginStateService: MzicLoginStateService;
    registrationStateService: RegistrationStateService;
  },
) {
  console.log(
    `[Registration Cleanup] Cleaning up incomplete registration. Reason: ${reason}`,
  );

  const {
    tokenService,
    artistLocalService,
    loginStateService,
    registrationStateService,
  } = services;

  // Clear ALL authentication and registration data
  tokenService.clearAlltokens(); // This includes token registration state
  artistLocalService.removeDataUSerTo2FA();
  loginStateService.clearState();
  registrationStateService.clearRegistrationState();

  // Clear any other user-specific cached data
  localStorage.removeItem('user-preferences');
  localStorage.removeItem('mzic-viberate-uuid');

  console.log(
    `[Registration Cleanup] All incomplete registration data cleared`,
  );
}

/**
 * Extracts current language from path
 */
function getCurrentLanguage(path: string): string {
  const langMatch = path.match(/^\/(en|pt-br|pt)/);
  return langMatch ? langMatch[1] : 'en';
}
